import { effect, Injectable, Injector, signal, runInInjectionContext, EffectRef } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AiEvent, StreamService } from '../stream.service';
import { Message } from '../../components/chat/chat.component';
import { Mutex } from '../mutex.service';
import { OauthService } from '../oauth.service';
import { QueueService } from '../queue.service';
import { RepoService } from '../repo.service';
import { DbService } from '../db.service';
import { WorkflowService, WorkflowType, WorkflowUpdate } from '../workflow.service';
import { SdtmService } from './sdtm.service';
import { UsdmService } from './usdm.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AiResponse } from '../../schema/frontendTypes';

type Status = "not_started" | "in_progress" | "completed" | "failed";

interface Item {
  name: string;
  description: string;
  required_info: string;
  status: Status;
  updates: string[];
}

export interface ActionItems {
  objective: string;
  action_items: Item[];
}

export interface Validation {
  analysis: string;
  current_item_updated_status: 'in_progress' | 'completed' | 'failed';
  current_item_new_updates: string[];
  update_remaining_action_items: Item[];
}

export type AiWritingChunk = {
  chunk: string;
  section: 'file' | 'shell' | 'mcp' | 'message' | 'question' | 'answer' | null;
  uri: string | null;
  fullContent: string | null;
}

export type ContextStart = {
  messageId: string;
  source: 'workflow' | 'summary'
}


@Injectable({
  providedIn: 'root',
})
export class AiService {
  constructor(
    private queue: QueueService,
    private mutex: Mutex,
    private auth: OauthService,
    private repoService: RepoService,
    private StreamService: StreamService,
    private http: HttpClient,
    private injector: Injector,
    private wfService: WorkflowService,
    private db: DbService,
  ) { }

  private aiIsWritingSubject = new BehaviorSubject<boolean>(false);  // TODO: convert it to a signal
  aiIsWriting$ = this.aiIsWritingSubject.asObservable();

  private aiWritingSectionSubject = new BehaviorSubject<string>('message');
  aiWriteSection$ = this.aiWritingSectionSubject.asObservable();

  private aiWritingChunkSubject = new BehaviorSubject<AiWritingChunk>({
    chunk: '',
    section: null,
    uri: null,
    fullContent: null,
  });
  aiWritingChunk$ = this.aiWritingChunkSubject.asObservable();

  private refreshSubject = new BehaviorSubject<void>(undefined);
  refresh$ = this.refreshSubject.asObservable();

  aiViewSubject = new BehaviorSubject<string>("");
  aiView$ = this.aiViewSubject.asObservable();

  private actionItemsSignal = signal<ActionItems | null>(null);
  get actionItems() {
    return this.actionItemsSignal();
  }

  session_label: string | null = null;
  internalHistory: Message[] = [];

  contextStart: ContextStart | null = null;

  workflowState: BehaviorSubject<WorkflowUpdate | null> = new BehaviorSubject<WorkflowUpdate | null>(null);
  workflowState$ = this.workflowState.asObservable();
  private workflowEffect?: EffectRef;

  nextMessage: Message | null = null;

  setAiIsWriting(isWriting: boolean) {
    this.aiIsWritingSubject.next(isWriting);
  }

  getAiIsWritingValue(): boolean {
    return this.aiIsWritingSubject.value;
  }

  async handleAiEvent(event: AiEvent): Promise<string | null> {
    let closedSection: string | null = null;
    switch (event.action) {
      case 'open':
        if (event.section === 'file') {
          this.aiWritingSectionSubject.next(`file:${event.path!}`);
        } else if (event.section === 'shell') {
          this.aiWritingSectionSubject.next('shell');
        } else if (event.section === 'mcp') {
          this.aiWritingSectionSubject.next('mcp');
        } else if (event.section === 'question') {
          this.aiWritingSectionSubject.next('question');
        } else if (event.section === 'answer') {
          this.aiWritingSectionSubject.next('answer');
        } else {
          this.aiWritingSectionSubject.next('message');
        }
        break;
      case 'close':
        const currentSection = event.section;
        this.aiWritingSectionSubject.next(`close:${currentSection}`);
        this.aiWritingChunkSubject.next({
          chunk: '',
          section: (['message', 'shell', 'mcp', 'question', 'answer', null].includes(currentSection) ? currentSection as 'message' | 'shell' | 'mcp' | 'question' | 'answer' | null : 'file'),
          uri: event.path,
          fullContent: event.content,
        });

        if (event.section === 'question' || event.section === 'answer') {
          this.queue.add({
            type: event.section as 'question' | 'answer',
            content: event.content || '',
          });
        }
        closedSection = event.section;
        break;
      case 'view':
        this.aiViewSubject.next(event.path!);
        break;
      default:
        throw new Error(`Unknown AI event action: ${event.action}`);
    }
    return closedSection;
  }

  askAnswered: boolean = false;
  async handleAsk(message: Message) {
    // const lastMsgIdx = details.history.length - 1;
    let failureCount = 0;
    let finalResponse: string | null = null;
    this.setAiIsWriting(true);
    // const lastUserMessage = [...details.history]
    //   .reverse()
    //   .find(m => m.role.toLowerCase().includes('user')) as Message | undefined;

    // if (lastUserMessage) {
    this.internalHistory.push({
      role: message.role,
      content: message.content,
      session_id: message.session_id,
      message_id: message.message_id || crypto.randomUUID(),
      internal: message.internal || false,
    });
    // }
    // console.log("user history:", JSON.stringify(this.internalHistory));
    while (this.getAiIsWritingValue() && !this.askAnswered) {
      this.aiWritingSectionSubject.next('message');
      // TODO: SEND INTERNAL HISTORY
      let context: Message[];
      if (this.contextStart) {
        const contextIdx = this.internalHistory.findIndex(m => m.message_id === this.contextStart?.messageId);
        context = contextIdx !== -1 ? this.internalHistory.slice(contextIdx) : this.internalHistory;
      } else {
        context = this.internalHistory;
      }
      const stream = await this.StreamService.getAssistantStream(context, '/chat', this.auth.user?.access_token || null, this.session_label);

      this.internalHistory.push({
        role: 'assistant',
        content: '',
        session_id: message.session_id,
        message_id: crypto.randomUUID(),
        internal: false,
      });
      try {
        for await (const chunk of stream) {
          this.internalHistory[this.internalHistory.length - 1].content += chunk.content;
          const release = await this.mutex.acquire();
          try {
            if (!this.getAiIsWritingValue()) break;

            this.aiWritingChunkSubject.next({
              chunk: chunk.content,
              section: ['message', 'shell', 'mcp', 'question', 'answer', null].includes(chunk.ai_write_section) ? chunk.ai_write_section as 'message' | 'shell' | 'mcp' | 'question' | 'answer' | null : 'file',
              uri: !(['message', 'shell', 'mcp', 'question', 'answer', null].includes(chunk.ai_write_section)) ? chunk.ai_write_section : null,
              fullContent: null,
            });

            if (chunk.event) {
              // release();
              const closedSection = await this.handleAiEvent(chunk.event);
              if (closedSection && ['question', 'answer', 'mcp', 'shell'].includes(closedSection)) {
                break;
              }
            }
          } finally {
            release();
          }
          failureCount = 0;
        }

        const startIdx = this.contextStart
          ? this.internalHistory.findIndex(
              m => m.message_id === this.contextStart!.messageId
            )
          : 0;
        const contextLength = startIdx !== -1
          ? this.internalHistory.length - startIdx
          : this.internalHistory.length;

        if (
          contextLength > environment.messageLength &&
          (!this.contextStart || this.contextStart.source === 'summary')
        ) {
          const sliceStart = startIdx !== -1 ? startIdx : 0;
          const slicedMessages = this.internalHistory.slice(
            sliceStart,
            sliceStart + environment.slicedLength // TODO: handle context managing
          );
          const response = await firstValueFrom(this.http
            .post<{
              summary: string;
            }>(`${environment.backendUrl}/chat/summarize`, slicedMessages)
          );
          const summaryMessage = {
            role: 'user',
            content: response.summary,
            session_id: this.internalHistory[0].session_id,
            message_id: crypto.randomUUID(),
            internal: true,
          };

          const previousSlicedMessages = startIdx !== -1 ? this.internalHistory.slice(0, startIdx) : [];

          this.internalHistory = [
            ...previousSlicedMessages,
            ...slicedMessages,
            summaryMessage,
            ...this.internalHistory.slice(sliceStart + environment.slicedLength),
          ];
          this.contextStart = { messageId: summaryMessage.message_id, source: 'summary' };
        }
        try {
          const repoId = this.repoService.repo()?.id;
          if (repoId && /^\d+$/.test(repoId)) {
            await this.db.upsertInternalHistory({
              session_id: message.session_id,
              history: this.internalHistory,
              repo_id: repoId,
              label: this.session_label,
            });
          }
          // console.log('UI history updated successfully');
        } catch (error) {
          console.error('Error updating UI history:', error);
        }
      } catch (error: unknown) {
        console.error('Error during AI ask handling:', error);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((error as any).cause?.code.startsWith('CRITICAL')) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          throw new Error((error as any).message, { cause: { code: (error as any).cause.code } });
        }
        failureCount++;
        if (failureCount >= 4) {
          this.setAiIsWriting(false);
          throw new Error('Too many failures.', { cause: { code: 'TOO_MANY_AI_FAILURES', error } });
        }

        this.queue.add({
          type: 'error',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          content: (error as any).error?.detail ? (error as any).error.detail : JSON.stringify(error),
        })
      } finally {
        // done
      }
      // console.log("HISTORY:", JSON.parse(JSON.stringify(this.internalHistory)));

      // create feedback
      const release = await this.mutex.acquire();
      const { allEvents: queue, shouldReact } = this.queue.getAll();
      release();

      let systemResponse = '<system_response>\n';
      let hasResponse = false;
      let toolCalls: string[] = [];
      for (const actionEvent of queue) {
        if (actionEvent.type === 'view') {
          systemResponse += `Viewed file: ${actionEvent.filename}\n`;
          systemResponse += actionEvent.output + '\n';
        } else if (actionEvent.type === 'createFile') {
          systemResponse += `Created file: ${actionEvent.filename}\n`;
        } else if (actionEvent.type === 'command' && actionEvent.viewOutput) {
          // systemResponse += `Ran command: ${actionEvent.command}\n`;
          // systemResponse += actionEvent.output + '\n';
          toolCalls.push(`Ran command: ${actionEvent.command}\n${actionEvent.output}\n`);
        } else if (actionEvent.type === "mcpToolResponse") {
          // systemResponse += `MCP Tool \`${actionEvent.tool}\` Response: ${actionEvent.output}\n`;
          toolCalls.push(`MCP Tool \`${actionEvent.tool}\` Response: ${actionEvent.output}\n`);
        } else if (actionEvent.type === 'error') {
          systemResponse += `Error: ${actionEvent.content}\n`;
        } else if (actionEvent.type === 'question' || actionEvent.type === 'answer') {
          hasResponse = true;
        }
      }
      if (toolCalls.length > 1) {
        systemResponse += `Too many tool calls!! You are allowed to call limited tools per turn.\nHere is the first one:\n`;
        toolCalls = toolCalls.slice(0, 1); // Limit to one tool call
      }
      toolCalls.forEach(toolCall => {
        systemResponse += `${toolCall}\n`;
      });
      systemResponse += '</system_response>';
      // console.log("System response:", systemResponse);

      if (shouldReact && !hasResponse) {
        this.internalHistory.push({
          role: 'user',
          content: systemResponse,
          session_id: message.session_id,
          message_id: crypto.randomUUID(),
          internal: true,
        } as Message);
      } else if (!shouldReact && !hasResponse) {
        this.internalHistory.push({
          role: 'user',
          content: '<no_tool_call/>',
          session_id: message.session_id,
          message_id: crypto.randomUUID(),
          internal: true,
        } as Message);
      } else {
        this.setAiIsWriting(false);
        const assistantMessages = this.internalHistory.filter(m => m.role === 'assistant');
        finalResponse = assistantMessages.length > 0 ? assistantMessages[assistantMessages.length - 1].content : null;
        break;
      }

    }
    return finalResponse ? this.parseAiResponse(finalResponse) : null;
  }

  private parseAiResponse(response: string | null): AiResponse {
    let astRespType: 'answer' | 'query' | null = null;
    let astContent: string | null = null;

    if (typeof response === 'string') {
      const tagMatch = response.match(/<(provideAnswer|requireClarification)>([\s\S]*)<\/\1>/i);
      if (tagMatch) {
        astRespType = tagMatch[1] === 'provideAnswer' ? 'answer' : 'query';
        astContent = tagMatch[2].trim();
      }
    }

    return {
      type: astRespType,
      content: astContent || 'Could not get result'
    };
  }

  async handleWorkflow(message: Message, workflow: WorkflowType) {
    let service!: UsdmService | SdtmService;
    if (workflow.type === 'sdtm') {
      service = this.injector.get(SdtmService);
    } else if (workflow.type === 'usdm') {
      service = this.injector.get(UsdmService);
    }
    await this.handleWorkflowService(message, workflow, service);
  }

  async handleWorkflowService(message: Message, workflow: WorkflowType, service: UsdmService | SdtmService): Promise<void> {
    this.workflowEffect?.destroy();
    // Use runInInjectionContext to properly use effect() outside constructor
    runInInjectionContext(this.injector, () => {
      this.workflowEffect = effect(() => {
        this.workflowState.next(service.workflowUpdate());
      });
    });
    this.setAiIsWriting(true);
    // this.nextMessage = message;
    this.internalHistory.push(message);
    if (!service.appState) {
      // @ts-expect-error - workflow type mismatch
      const { nextPrompt, setNewContext } = await service.handleAiAnswer({ type: 'answer', content: message.content }, workflow);
      if (!nextPrompt) {
        throw new Error('No next prompt found in SDTM workflow state', { cause: { code: "CRITICAL_PROMPT_NOT_FOUND" } });
      }
      if (setNewContext) {
        this.contextStart = { messageId: crypto.randomUUID(), source: 'workflow' };
      }
      const nextPromptMsg = ({
        role: 'user',
        content: nextPrompt,
        session_id: message.session_id,
        message_id: this.contextStart ? this.contextStart.messageId : crypto.randomUUID(),
        internal: true,
      }) as Message;
     this.nextMessage = nextPromptMsg;
    }
    while (this.getAiIsWritingValue()) {
      let aiMsg: AiResponse | null = { type: "answer", content: "" };
      if (this.nextMessage!.content !== "SKIP") {
        aiMsg = await this.handleAsk(this.nextMessage!);
      }
      if (!aiMsg) break;
      if (aiMsg.type !== 'answer') break;
      this.setAiIsWriting(true);
      let nextPrompt: string | null;
      let setNewContext: boolean | null;
      try {
        // @ts-expect-error - workflow type mismatch
        const result = await service.handleAiAnswer(aiMsg, workflow);
        nextPrompt = result.nextPrompt;
        setNewContext = result.setNewContext;
      } catch (error) {
        console.error('Error handling workflow answer:', error);
        // @ts-expect-error - workflow type mismatch
        const result = await service.skipCurrentState(workflow.data.protocolFilePath);
        nextPrompt = result.nextPrompt || null;
        setNewContext = result.setNewContext;
      }
      // this.workflowState.next(currentState || null);
      if (setNewContext) {
        this.contextStart = { messageId: crypto.randomUUID(), source: 'workflow' };
      }
      if (service.appState?.current_state === 'done' || !nextPrompt) {
        this.setAiIsWriting(false);
        service.cleanup();
        this.wfService.setWorkflow(null);
        this.nextMessage = null;
        break;
      }
      this.nextMessage = {
        role: 'user',
        content: nextPrompt,
        session_id: this.internalHistory.length > 0 ? this.internalHistory[0].session_id : crypto.randomUUID(),
        message_id: setNewContext && this.contextStart ? this.contextStart.messageId : crypto.randomUUID(),
        internal: true
      } as Message;
    }

    this.setAiIsWriting(false);
  }

  async getSessionLabel(sessionId: string) {
    let nRetries = 0;
    while (!this.session_label && nRetries < 5) {
      nRetries++;
      await new Promise(resolve => setTimeout(resolve, 5000));
      try {
        const data = await this.db.getUiHistory(sessionId);
        if (data.label) {
          this.session_label = data.label;
          // Notify other components that the session label was updated
          this.repoService.notifySessionLabelUpdated(sessionId, data.label);
          return;
        }
      } catch (error) {
        console.error('Error fetching session label:', error);
      }
    }
    if (nRetries >= 5) {
      console.error('Failed to fetch session label after 5 retries');
    }
  }
}
